import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Building2, Droplets, LogIn, MessageSquareText, Sprout, Tractor } from 'lucide-react';
import { Icon } from '../ui/Icon';
import { EmptyState } from '../ui/EmptyState';
import { useAuth } from '../../auth/context';
import { usePersona } from '../auth/RoleContext';
import {
  ROLE_SPECS,
  homeFor,
  ownFarms,
  type OwnFarm,
} from '../auth/personas';
import { defineMessages } from '../../i18n/define';
import { describeApiError, LanguageSwitcher, useIntlSafe, useLocale, useT } from '../../i18n';
import { COMMON } from '../../i18n/common';
import '../auth/auth.css';

const m = defineMessages({
  rejected: { id: 'auth.signIn.rejected', defaultMessage: 'вход отклонён сервером' },
  account: { id: 'auth.signIn.account', defaultMessage: 'Учётная запись' },
  password: { id: 'auth.signIn.password', defaultMessage: 'Пароль' },
  signingIn: { id: 'auth.signIn.busy', defaultMessage: 'Входим…' },
  otherAccount: { id: 'auth.farm.otherAccount', defaultMessage: 'Выйти и войти другим аккаунтом' },
  noFarmTitle: { id: 'auth.farm.noneTitle', defaultMessage: 'Аккаунт не связан с хозяйством' },
  noFarmReason: {
    id: 'auth.farm.noneReason',
    defaultMessage: 'Сервер не назвал ни одного хозяйства аккаунта {username}: кабинет хозяйства открыть не для чего. Привязку делает администратор.',
  },
  pickTitle: { id: 'auth.farm.pickTitle', defaultMessage: 'Какое из ваших хозяйств открыть' },
  pickCaption: {
    id: 'auth.farm.pickCaption',
    defaultMessage:
      '{count, plural, one {Аккаунт связан с # хозяйством} few {Аккаунт связан с # хозяйствами} many {Аккаунт связан с # хозяйствами} other {Аккаунт связан с # хозяйствами}}; список — из ответа сервера, чужое хозяйство здесь не выбирается.',
    description: 'Творительный падеж: «с 1 хозяйством», «с 3 хозяйствами»',
  },
  pointWorks: { id: 'auth.login.pointWorks', defaultMessage: 'Работы агроцикла, сроки и отчёты хозяйств' },
  pointMap: { id: 'auth.login.pointMap', defaultMessage: 'Вода, карантин и сдача урожая — на одной карте' },
  pointAi: { id: 'auth.login.pointAi', defaultMessage: 'ИИ-аналитик отвечает по данным района' },
  station: { id: 'auth.login.station', defaultMessage: 'аналитическая станция' },
  tagline: { id: 'auth.login.tagline', defaultMessage: 'Агроаналитика района: от поля до доклада' },
  heading: { id: 'auth.login.heading', defaultMessage: 'Вход' },
  lead: { id: 'auth.login.lead', defaultMessage: 'Учётную запись выдаёт администратор платформы.' },
});

/* --------------------------------------------------------------- вход */

/** Форма входа: username и пароль уходят в канонический `/auth/login`. */
function SignIn() {
  const { signIn } = useAuth();
  const intl = useIntlSafe();
  const t = intl.formatMessage;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(username, password);
    } catch (cause) {
      // Причина словами: «не получилось» без причины заставляет гадать.
      // Текст — по коду ошибки на языке интерфейса (ADR-0019); на русском —
      // слова сервера, они конкретнее.
      setError(
        cause instanceof Error
          ? describeApiError(intl, { code: (cause as { code?: string }).code, message: cause.message })
          : t(m.rejected),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="signin" onSubmit={submit}>
      <label className="signin__field">
        <span className="t-label">{t(m.account)}</span>
        <input
          type="text"
          autoComplete="username"
          autoFocus
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          required
        />
      </label>
      <label className="signin__field">
        <span className="t-label">{t(m.password)}</span>
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
      </label>
      <button type="submit" className="btn btn--primary focus-ring" disabled={busy}>
        <Icon icon={LogIn} size={16} />
        {busy ? t(m.signingIn) : t(COMMON.signIn)}
      </button>
      {error ? (
        <p className="signin__error" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

/* ------------------------------------------------------------------- шаг 2 */

/**
 * Второй шаг входа аккаунта хозяйства — только из СВОИХ хозяйств.
 *
 * Хозяйство отдаёт `/auth/me` (`principal.actors`). Одно — вопроса нет, и
 * этот шаг не показывается вовсе. Несколько — выбирается одно из них. Ни
 * одного — кабинет показывать нечем, и это говорится словами: подставить
 * «демонстрационное» чужое хозяйство значило бы открыть кабинет, в котором
 * сервер откажет на каждой записи.
 */
function OwnFarmPicker({ onBack }: { onBack: () => void }) {
  const navigate = useNavigate();
  const { principal } = useAuth();
  const { setPersona } = usePersona();
  const t = useT();
  const farms = ownFarms(principal?.actors ?? []);

  function pick(farm: OwnFarm) {
    setPersona({ role: 'farmer', farmId: farm.farmId, farmName: farm.farmName });
    navigate(ROLE_SPECS.farmer.home, { replace: true });
  }

  return (
    <div className="pickfarm">
      <button type="button" className="btn btn--ghost btn--sm focus-ring" onClick={onBack}>
        <Icon icon={ArrowLeft} size={16} />
        {t(m.otherAccount)}
      </button>

      {farms.length === 0 ? (
        <EmptyState
          compact
          title={t(m.noFarmTitle)}
          reason={t(m.noFarmReason, { username: principal?.username ?? '' })}
        />
      ) : (
        <>
          <div className="pickfarm__head">
            <h2 className="t-title">{t(m.pickTitle)}</h2>
            <p className="t-caption">{t(m.pickCaption, { count: farms.length })}</p>
          </div>
          <ul className="pickfarm__list">
            {farms.map((farm) => (
              <li key={farm.farmId}>
                <button type="button" className="pickfarm__hit focus-ring" onClick={() => pick(farm)}>
                  <span className="pickfarm__icon" aria-hidden="true">
                    <Icon icon={Building2} size={16} />
                  </span>
                  <span className="pickfarm__main">
                    <span className="pickfarm__name">{farm.farmName}</span>
                  </span>
                  <Icon icon={ArrowRight} size={16} />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- экран */

/** Что умеет платформа — три строки вместо устаревших цифр прототипа. */
const POINTS = [
  { icon: Tractor, text: m.pointWorks },
  { icon: Droplets, text: m.pointMap },
  { icon: MessageSquareText, text: m.pointAi },
];

export function Login() {
  const { persona, loading, signOut } = usePersona();
  const t = useT();
  const { locales } = useLocale();
  // Аккаунт хозяйства без открытого хозяйства (не связан ни с одним или
  // связан с несколькими и ещё не выбрал) идёт на второй шаг сразу.
  const needsFarm = persona?.role === 'farmer' && !persona.farmId;
  const shown = needsFarm ? 'farm' : 'role';

  // Вошедший на экране входа не задерживается: он идёт в дом своей роли.
  if (!loading && persona && !persona.anonymous && !needsFarm) {
    return <Navigate to={homeFor(persona.role)} replace />;
  }

  return (
    <main className="login">
      <div className="login__panel">
        <section className="login__hero on-green">
          <div className="login__brand">
            <span className="login__mark" aria-hidden="true">
              <Icon icon={Sprout} size={20} />
            </span>
            <span className="login__brandname">Agro AI</span>
          </div>

          <h1 className="login__title">
            Agro AI — <span className="login__titlesoft">{t(m.station)}</span>
          </h1>
          <p className="login__sub">{t(m.tagline)}</p>

          <ul className="login__points">
            {POINTS.map((point) => (
              <li key={point.text.id}>
                <span className="login__pointicon" aria-hidden="true">
                  <Icon icon={point.icon} size={16} />
                </span>
                {t(point.text)}
              </li>
            ))}
          </ul>
        </section>

        <section className="login__body">
          {/* Ряд языка — только когда выбирать есть из чего: пустой ряд
              добавил бы колонке лишний отступ сверху. */}
          {locales.length > 1 ? (
            <div className="login__langrow">
              <LanguageSwitcher />
            </div>
          ) : null}
          {shown === 'role' ? (
            <>
              <div className="login__ask">
                <h2 className="t-title">{t(m.heading)}</h2>
                <p className="login__leadtext">{t(m.lead)}</p>
              </div>
              <SignIn />
            </>
          ) : (
            <OwnFarmPicker onBack={signOut} />
          )}
        </section>
      </div>
    </main>
  );
}

export default Login;
